/**
 * Ski scoring rollout mode (docs/features/ski-scoring-v3.md section 19).
 *
 * `SKI_SCORING_ENGINE` selects how the live and rescore writers score piste,
 * off-piste and touring:
 * - "v2": the legacy engines only, publishing revision 2 exactly as before.
 * - "shadow" (the default when unset): publishes revision 2 exactly as "v2",
 *   and also runs the v3 engine in the same pass. The v3 output goes only to
 *   the score store (behind its own flags) and to a compact comparison in the
 *   cron summary, never into the published rows.
 * - "v3": publishes revision 3 from the v3 engine.
 * - "v2-rollback": the rollback once revision 3 has been published. Runs the
 *   legacy engines exactly as "v2" (no v3 pass, no shadow comparison, no v3
 *   facts, checkpoints or input frames) and publishes the same output under
 *   revision 4, because the guard trigger refuses to lower a stored revision
 *   from 3. Only the revision stamps differ from "v2".
 *
 * The database guard trigger refuses to lower a sport revision on a row that
 * already exists, so moving to "v3" is a deliberate owner flip. A rollback
 * after revision 3 has been published must use "v2-rollback" (the old engine
 * under revision 4); switching this variable back to "v2" or "shadow" alone
 * would make every ski upsert touching a revision-3 row fail.
 *
 * Before the flip to "v3":
 * - run lib/conditions/__tests__/ski-v3-flip-readiness.test.ts with
 *   SKI_SCORING_ENGINE=v3; it checks that writers which certify scoped inputs
 *   or stamp aggregates use the exact and minimum-revision helpers, because
 *   readers accept revisions 2, 3 and 4 during the transition;
 * - reset SKI_V2_READABLE_UNTIL (the readableUntil of the ski entry in
 *   SPORT_TRANSITION_WINDOWS, sport-scoring-versions.ts) to about 45 days
 *   after the flip.
 *
 * Rolling back after the first "v3" publish ("v2-rollback"):
 * - run the same flip-readiness test with SKI_SCORING_ENGINE=v2-rollback (its
 *   source checks run in "v3" and "v2-rollback");
 * - move SKI_V2_READABLE_UNTIL (the ski entry of SPORT_TRANSITION_WINDOWS) to
 *   about 45 days after the rollback, so the revision 2 and 3 rows stay
 *   readable next to the new revision 4 rows (the ski window lists 2, 3 and
 *   4); after that date only revision 4 is readable in this mode;
 * - set SKI_SCORING_ENGINE=v2-rollback, unset SCORE_STORE_READERS and
 *   redeploy (runbook Rollback step 2: no score store revision holds the whole
 *   history during a rollback, so the history pages go back to the legacy
 *   readers). Writers stamp revision 4 (`SPORT_SCORING[sport].version`,
 *   `skiPublishRevision`); the v3-only paths (v3 context, evidence and luck
 *   reads, shadow comparison, score store fact and checkpoint writes, input
 *   frames) stay off, as in "v2". Scripts must get the mode from the shell,
 *   not only from .env.local, because SPORT_SCORING fixes the revision before
 *   .env.local is read (skiScoringRevisionDrift in sport-scoring-versions.ts
 *   detects this);
 * - with CONDITIONS_SCORE_STORE_WRITE_ENABLED="true" the live route registers
 *   revision 4 of piste, off-piste and touring in score_revisions under the
 *   legacy fingerprint `legacy:<sport>:4` (ski-live/store-writes.ts
 *   registerSkiRollbackRevisions). registerRevision refuses any other
 *   fingerprint for revision 4, and the replay refuses legacy revisions, so no
 *   v3 fact is ever written under it;
 * - no live writer produces score_day facts for a legacy revision: they come
 *   only from scripts/copy-legacy-scores-to-score-day.ts, which copies closed
 *   days whose stored sport revision equals a revision a live or history
 *   pointer serves. In this mode new rows are stamped 4 and the v3-era rows
 *   carry 3, so revision 2 never gains another day. Pointing back to it
 *   (scripts/flip-ski-v3.ts --revision=2, only while it is not retired) serves
 *   history that stops at the v3 flip: a short bridge at most, not a target;
 * - the target is revision 4. Move the pointers with scripts/flip-ski-v3.ts
 *   --revision=4 --apply --confirm-mode=v2-rollback (the full flip of all
 *   three sports through flipScorePublicationsWithAwards; with --live-only the
 *   copy would also write into the revision 3 history pointer). Then run the
 *   legacy copy over the closed dates since the rollback, repeat it while the
 *   rollback lasts, and queue the award rebuild again after each copy (a
 *   repeated flip-ski-v3.ts --apply with the pointers already on revision 4
 *   only runs enqueueAwardRepublish). Revision 4 has no score_day facts until
 *   the first copy, so its store history starts at the rollback;
 * - before SKI_V2_READABLE_UNTIL passes, rescore the retained window in this
 *   mode so those rows carry revision 4 (or move the date again): after it,
 *   the revision 2 and 3 rows drop out of every reader in this mode;
 * - never set the mode back to "v2" or "shadow" (4 to 2) or to "v3" (4 to 3)
 *   once revision 4 rows exist: the guard refuses both. Returning to the v3
 *   engine afterwards needs a new revision number (5) in code.
 *
 * Revision 5 and the v3 engine revision (SKI_V3_ENGINE_REVISION, lane
 * feat/p3i-rev5-mechanics, docs/handover/phase-2c.md "Revision 5 mechanics"; the
 * chain modes are W8(a) to (d), 13 October 2026):
 * - the v3 engine runs one formula per process, chosen when the process loads
 *   by SKI_V3_ENGINE_REVISION: "3" (the default when unset) runs the revision 3
 *   parameter set, "5" the revision 5 set and "6" (the rollback alias of
 *   revision 3) revision 3's set. lib/conditions/ski/params.ts selects
 *   SKI_V3_PARAMS from it, so an unset variable leaves every revision 3 output
 *   byte-identical. Revision 5 is revision 3's formula (the consistency
 *   switch, W8 step 1 and decision D-e), so all three run one formula;
 * - the row revision the writers stamp in "v3" and the store revision of the
 *   live score_day facts and snow_checkpoints follow from it (3 and 3, 5 and
 *   5, or 6 and 6). "v2-rollback" stamps 4 and runs only under engine revision
 *   3: revision 7, drafted as the legacy engines after revision 5, is retired
 *   (W8(d); the 6 October 2026 decisions abandon the v2 rollback), so
 *   "v2-rollback" under 5 or 6 is not deployable (skiV3EngineRevisionDeployProblem);
 * - a second snowpack chain (W8(a) and (c)): SKI_V3_SHADOW_REVISION names the
 *   revision whose facts and checkpoints the same live process also writes on
 *   every ski slice, from its own chain (its own snow_checkpoints), never
 *   published and never stamped on a row: 5 beside engine revision 3 (the
 *   "v5-shadow" mode: revision 3 published, revision 5 gathering its own live
 *   chain from the replay and reseed checkpoints before its flip) and 3 beside
 *   engine revision 5 (the "v5" mode: revision 5 published, revision 3 kept as
 *   a live shadow, so the alias 6 rollback re-serves revision 3 as it would
 *   have been published; a hard precondition of the revision 5 flip, which
 *   W7's flip tool enforces). A second chain only resumes from a stored
 *   checkpoint of its own revision (never the v2 bootstrap or a cold start)
 *   and runs the one loaded formula, so it needs a revision of the same
 *   formula (ski-live/chains.ts refuses otherwise); the alias 6 runs none;
 * - the named modes, in "v3": engine 3 alone "r3" (production today); 3 with
 *   shadow 5 "v5-shadow"; 5 with shadow 3 "v5"; 5 alone "v5-solo" (only once
 *   revision 3 has been released as previous history); 6 alone "alias-6";
 * - a revision 5 replay, reseed and flip run from a process started with
 *   SKI_V3_ENGINE_REVISION=5 (scripts read it from the shell, like
 *   SKI_SCORING_ENGINE), whatever the deployment runs;
 * - rolling back a revision 5 publish (decision D3, 6 October 2026) moves
 *   upwards to the registered alias revision 6 carrying revision 3's stored
 *   formula string byte for byte (registered only by registerSkiAliasRevisions,
 *   score-store/revisions.ts, W8 guard 5): revision 3's published facts are
 *   copied into it by copy_score_day_revision (scripts/copy-ski-alias-6.ts,
 *   revision 3 into 6 only, W8 guard 1), never replayed, then the pointers
 *   flip upwards (W7) and the "alias-6" deployment writes its live facts and
 *   checkpoints under revision 6. Its chain resumes from its own checkpoints
 *   and, until it has newer ones inside the look-back, from revision 3's (the
 *   chain the "v5" mode kept running), so the alias continues revision 3's
 *   chain (runbook "Rollback from revision 5"). Revision 6 facts are written
 *   only by a process that loaded engine revision 6 (store-writes.ts, checked
 *   by revision number), and the live route refuses the "alias-6" mode until
 *   the alias is registered with revision 3's stored string and ski rows carry
 *   revision 5 (every live pointer on 5 or 6, or a recent engine 5 slice;
 *   ski-live/chains.ts readSkiAlias6WriteProblem, W8 review B M1 and m1):
 *   rows stamped 6 before any row carries revision 5 would refuse revision 5
 *   for good.
 *
 * SKI_SCORING_ENGINE is matched exactly. Any other value (a typo such as
 * "v2_rollback" included) falls back to "shadow", which stamps revision 2: the
 * live route reports it as a degraded input, and once revision 3 or 4 has
 * been published the guard refuses those ski writes, so a typo stops ski
 * updates rather than lowering a revision.
 *
 * This module has no imports on purpose: `sport-scoring-versions.ts` reads it
 * while building `SPORT_SCORING`, so it must never import that module back.
 */

export type SkiScoringEngineMode = "v2" | "shadow" | "v3" | "v2-rollback";
/** Modes in which the v3 engine runs (shadow or publish). */
export type SkiV3RunningMode = "shadow" | "v3";
export type SkiRevisionedSport = "piste" | "off-piste" | "touring";

export const SKI_SCORING_ENGINE_ENV = "SKI_SCORING_ENGINE";
export const DEFAULT_SKI_SCORING_ENGINE_MODE: SkiScoringEngineMode = "shadow";

/** Sport revision written by the legacy (snow-state-v2) engines. */
export const SKI_V2_REVISION = 2;
/** Sport revision written by the v3 (snow-state-v3) engine at its first formula (engine revision 3, the default). */
export const SKI_V3_REVISION = 3;
/**
 * Sport revision written by the legacy engines in "v2-rollback" mode while the
 * v3 engine revision is 3: the same formula as revision 2, published above
 * revision 3 so the guard accepts it.
 */
export const SKI_V2_ROLLBACK_REVISION = 4;
/** The next v3 formula revision (revision 4 is the reserved v2-rollback revision). */
export const SKI_V3_REVISION_5 = 5;
/**
 * The rollback alias of revision 3 after a revision 5 publish (decision D3): a
 * registered revision carrying revision 3's stored formula string byte for
 * byte (registerSkiAliasRevisions, W8 guard 5), filled by copies of revision 3's
 * published facts (copy_score_day_revision, W8 guard 1) and, once the "alias-6"
 * deployment runs (SKI_V3_ENGINE_REVISION=6, W8(a)), by its own live facts and
 * checkpoints, above 5 so the guard accepts its rows.
 */
export const SKI_V3_R3_ALIAS_REVISION = 6;
/**
 * The legacy revision drafted for "v2-rollback" after revision 5 (the legacy engines above 5 and
 * 6). Retired (W8(d), 13 October 2026): no writer stamps it, no flip targets it
 * (scripts/flip-ski-v3.ts refuses it), and "v2-rollback" runs only under engine revision 3. It
 * stays reserved for the legacy fingerprint (score-store/revisions.ts), so no v3 fact can ever be
 * registered under it, and listed in the ski transition window, which costs nothing while no row
 * carries it.
 */
export const SKI_V2_ROLLBACK_REVISION_AFTER_R5 = 7;
/** Revisions whose scores come from the legacy engines (never v3 facts). */
export const SKI_LEGACY_ENGINE_REVISIONS: readonly number[] = Object.freeze([SKI_V2_REVISION, SKI_V2_ROLLBACK_REVISION, SKI_V2_ROLLBACK_REVISION_AFTER_R5]);
/** The legacy "v2-rollback" revisions, registered only with legacyEngineFingerprint (7 is retired but stays reserved). */
export const SKI_V2_ROLLBACK_REVISIONS: readonly number[] = Object.freeze([SKI_V2_ROLLBACK_REVISION, SKI_V2_ROLLBACK_REVISION_AFTER_R5]);

export const SKI_V3_ENGINE_REVISION_ENV = "SKI_V3_ENGINE_REVISION";
/** v3 engine revisions this module parses: 3 (default), 5, and 6 (revision 3's formula as the rollback alias). */
export type SkiV3EngineRevision = 3 | 5 | 6;
/** The formula revisions behind them (the parameter sets in lib/conditions/ski/params.ts). */
export type SkiV3FormulaRevision = 3 | 5;
export const SKI_V3_ENGINE_REVISIONS: readonly SkiV3EngineRevision[] = Object.freeze([3, 5, 6]);
export const DEFAULT_SKI_V3_ENGINE_REVISION: SkiV3EngineRevision = 3;
/**
 * v3 engine revisions a deployment or a writing script may run: every revision this module parses
 * since W8(a) (13 October 2026), the alias 6 with its "alias-6" mode. Registration still refuses a
 * revision 6 except through registerSkiAliasRevisions (revision 3's stored string, W8 guard 5), and
 * the mode itself has its own deploy rules (skiV3EngineRevisionDeployProblem: "v3" only, and the
 * live route's publication check).
 */
export const SKI_V3_DEPLOYABLE_ENGINE_REVISIONS: readonly SkiV3EngineRevision[] = Object.freeze([3, 5, 6]);

export const SKI_V3_SHADOW_REVISION_ENV = "SKI_V3_SHADOW_REVISION";
/** Revisions a second chain may write (SKI_V3_SHADOW_REVISION): 5 beside engine 3, 3 beside engine 5. */
export type SkiV3ShadowRevision = 3 | 5;
/** The named live chain modes in "v3" (see the header). */
export type SkiV3ChainMode = "r3" | "v5-shadow" | "v5" | "v5-solo" | "alias-6";

export const SKI_REVISIONED_SPORTS: readonly SkiRevisionedSport[] = Object.freeze(["piste", "off-piste", "touring"]);

type Env = Readonly<Record<string, string | undefined>>;

export type SkiScoringEngineSetting = Readonly<{
  mode: SkiScoringEngineMode;
  /** False when the variable is set to a value this module does not know; the
   * mode then falls back to the default, which never publishes revision 3. */
  recognised: boolean;
}>;

function isSkiScoringEngineMode(value: string): value is SkiScoringEngineMode {
  return value === "v2" || value === "shadow" || value === "v3" || value === "v2-rollback";
}

/** The configured mode and whether the raw value was recognised (for cron summaries). */
export function skiScoringEngineSetting(env: Env = process.env): SkiScoringEngineSetting {
  const raw = env[SKI_SCORING_ENGINE_ENV];
  if (raw === undefined || raw.trim() === "") return { mode: DEFAULT_SKI_SCORING_ENGINE_MODE, recognised: true };
  const value = raw.trim();
  if (isSkiScoringEngineMode(value)) return { mode: value, recognised: true };
  return { mode: DEFAULT_SKI_SCORING_ENGINE_MODE, recognised: false };
}

export type SkiV3EngineRevisionSetting = Readonly<{
  revision: SkiV3EngineRevision;
  /** False when SKI_V3_ENGINE_REVISION holds a value this module does not know; the revision then falls back to 3. */
  recognised: boolean;
  /**
   * Whether the resolved revision may run in a deployment or a writing script
   * (SKI_V3_DEPLOYABLE_ENGINE_REVISIONS): every parsed revision since W8(a). An unrecognised value
   * resolves to 3, which may run; `recognised` reports the typo, as before. The modes have their own
   * rules on top (skiV3EngineRevisionDeployProblem).
   */
  deployable: boolean;
}>;

/**
 * The configured v3 engine revision. Unset or empty is 3. Exact "3", "5" or
 * "6" (surrounding whitespace ignored); anything else falls back to 3 and is
 * reported unrecognised. After a revision 5 publish a typo therefore stamps
 * revision 3, which the guard refuses on every row already at 5: ski updates
 * stop rather than lowering a revision, as for SKI_SCORING_ENGINE.
 */
export function skiV3EngineRevisionSetting(env: Env = process.env): SkiV3EngineRevisionSetting {
  const raw = env[SKI_V3_ENGINE_REVISION_ENV];
  if (raw === undefined || raw.trim() === "") return { revision: DEFAULT_SKI_V3_ENGINE_REVISION, recognised: true, deployable: true };
  const value = raw.trim();
  if (value === "3" || value === "5" || value === "6") {
    const revision = Number(value) as SkiV3EngineRevision;
    return { revision, recognised: true, deployable: isSkiV3EngineRevisionDeployable(revision) };
  }
  return { revision: DEFAULT_SKI_V3_ENGINE_REVISION, recognised: false, deployable: true };
}

/** True for a v3 engine revision a deployment or a writing script may run (3, 5 and, since W8(a), the alias 6). */
export function isSkiV3EngineRevisionDeployable(revision: number): boolean {
  return (SKI_V3_DEPLOYABLE_ENGINE_REVISIONS as readonly number[]).includes(revision);
}

export function skiV3EngineRevision(env: Env = process.env): SkiV3EngineRevision {
  return skiV3EngineRevisionSetting(env).revision;
}

/** The v3 engine revision this process loaded (lib/conditions/ski/params.ts selects its parameter set from it). */
export const SKI_V3_ENGINE_REVISION_AT_LOAD: SkiV3EngineRevision = skiV3EngineRevision();

/**
 * The v3 engine revision ski row stamps are built from (W8 step 2 review B, R2-1): the revision itself
 * when it is deployable, otherwise 3. Every parsed revision is deployable since W8(a), so this is the
 * identity today (6 stamps 6 in "v3"); it stays the one place SPORT_SCORING (and so every writer that
 * stamps from it: the backfill cron, the weekly and monthly aggregates, the live route and the rescore
 * scripts), skiPublishRevision and skiScoringRevisionDrift take the engine revision from, so a revision
 * ever made undeployable again stops stamping at once. A mode that may not run (the alias 6 outside
 * "v3", "v2-rollback" under 5 or 6) is refused by skiV3EngineRevisionDeployProblem where a writer can
 * stop; the stamp writers that cannot stop write what that mode stamps (6 outside "v3" stamps 2 in
 * "v2" and "shadow", and "v2-rollback" stamps 4), which the guard refuses on every row already above.
 */
export function skiV3StampEngineRevision(revision: SkiV3EngineRevision = SKI_V3_ENGINE_REVISION_AT_LOAD): SkiV3EngineRevision {
  return isSkiV3EngineRevisionDeployable(revision) ? revision : DEFAULT_SKI_V3_ENGINE_REVISION;
}

/**
 * Why this process must not write ski scores (null when it may), from SKI_SCORING_ENGINE and the v3
 * engine revision the environment names or this process loaded (W8(a) and (d), 13 October 2026):
 * - a revision this build may not run (none today: every parsed revision is deployable);
 * - the alias 6 outside "v3": it exists only to publish revision 3's formula as revision 6 after a
 *   revision 5 publish ("alias-6" mode), and a shadow or legacy mode would write revision 6 facts
 *   from a shadow run or stamp 2 over rows at 5;
 * - "v2-rollback" under 5 or 6: revision 7, its stamp there, is retired (W8(d)), and its 4 would only
 *   be refused over the rows revision 5 or 6 stamped. After a revision 5 flip the rollback is the
 *   alias 6.
 * The live route records it as the degraded input `ski_v3_engine_revision` and stops the ski slice
 * before any provider work or write; the ski rescore scripts throw it before they start; health warns
 * `ski-v3-engine-revision-undeployable`; scripts/check-sport-revisions.ts refuses the deploy. The
 * alias 6's write rule (registered with revision 3's stored string, and ski rows already carrying
 * revision 5) needs the database and is checked by the live route (ski-live/chains.ts
 * readSkiAlias6WriteProblem).
 */
export function skiV3EngineRevisionDeployProblem(env: Env = process.env, loaded: SkiV3EngineRevision = SKI_V3_ENGINE_REVISION_AT_LOAD): string | null {
  const setting = skiV3EngineRevisionSetting(env);
  if (!setting.deployable || !isSkiV3EngineRevisionDeployable(loaded)) {
    return `SKI_V3_ENGINE_REVISION=${!setting.deployable ? setting.revision : loaded} is not a v3 engine revision this build may run; ski writes nothing`;
  }
  const mode = skiScoringEngineMode(env);
  const named = setting.revision !== DEFAULT_SKI_V3_ENGINE_REVISION ? setting.revision : loaded;
  if ((setting.revision === SKI_V3_R3_ALIAS_REVISION || loaded === SKI_V3_R3_ALIAS_REVISION) && mode !== "v3") {
    return `SKI_V3_ENGINE_REVISION=${SKI_V3_R3_ALIAS_REVISION} (the revision 3 rollback alias) runs only with SKI_SCORING_ENGINE=v3, not "${mode}" ` +
      "(the \"alias-6\" mode publishes revision 3's formula as revision 6 after a revision 5 publish); ski writes nothing";
  }
  if (mode === "v2-rollback" && named !== DEFAULT_SKI_V3_ENGINE_REVISION) {
    return `SKI_SCORING_ENGINE=v2-rollback with SKI_V3_ENGINE_REVISION=${named}: revision 7 (the legacy engines after revision 5) is retired ` +
      "(W8(d)); ski writes nothing. After the revision 5 flip the rollback is the alias 6 (SKI_V3_ENGINE_REVISION=6 in v3)";
  }
  return null;
}

/**
 * Why the ski rescore scripts (scripts/rescore-all-core.ts, scripts/rescore-sports.ts) must not run
 * (null when they may): skiV3EngineRevisionDeployProblem, and the alias 6 in any mode (W8(a) and (d),
 * 13 October 2026). The alias re-serves revision 3's published facts through the 3 to 6 copy and the
 * "alias-6" deployment's live writer, never a rescore, and a rescore script cannot check that revision
 * 5 has been published before it stamps rows 6 (the live route does, ski-live/chains.ts).
 */
export function skiV3RescoreEngineProblem(env: Env = process.env, loaded: SkiV3EngineRevision = SKI_V3_ENGINE_REVISION_AT_LOAD): string | null {
  const deploy = skiV3EngineRevisionDeployProblem(env, loaded);
  if (deploy) return deploy;
  if (skiV3EngineRevisionSetting(env).revision === SKI_V3_R3_ALIAS_REVISION || loaded === SKI_V3_R3_ALIAS_REVISION) {
    return `SKI_V3_ENGINE_REVISION=${SKI_V3_R3_ALIAS_REVISION} (the revision 3 rollback alias) never rescores: the alias holds copies of revision 3's ` +
      "published facts and the \"alias-6\" deployment's live facts only (runbook \"Rollback from revision 5\"); ski writes nothing";
  }
  return null;
}

/**
 * Script guard: lib/conditions/ski/params.ts chose its parameter set when the process loaded, but
 * scripts read .env.local afterwards, so a SKI_V3_ENGINE_REVISION set only there would leave the
 * engine on another formula than the one named. Null when they agree; otherwise a message for the
 * script to throw before it writes anything.
 */
export function skiV3EngineRevisionDrift(env: Env = process.env): string | null {
  const configured = skiV3EngineRevision(env);
  if (configured === SKI_V3_ENGINE_REVISION_AT_LOAD) return null;
  return `SKI_V3_ENGINE_REVISION reads ${configured} but this process loaded the revision ${SKI_V3_ENGINE_REVISION_AT_LOAD} engine; set it in the shell environment for this command, not only in .env.local`;
}

export function isSkiV3EngineRevision(revision: number): revision is SkiV3EngineRevision {
  return revision === 3 || revision === 5 || revision === 6;
}

/** True for a v3 formula revision (3 or 5): one with its own parameter set. */
export function isSkiV3FormulaRevision(revision: number): revision is SkiV3FormulaRevision {
  return revision === 3 || revision === 5;
}

/** The formula a v3 engine revision runs: the alias 6 runs revision 3's. */
export function skiV3FormulaRevision(revision: SkiV3EngineRevision): SkiV3FormulaRevision {
  return revision === 5 ? 5 : 3;
}

/**
 * Revision of the score_day facts and snow_checkpoints a v3 engine revision writes: its own (3, 5,
 * and since W8(a) 6 for the alias, whose live facts and checkpoints go under the registered alias).
 * The replay, settle, reseed and the R0 tools of an engine 6 process therefore run nothing (their
 * revision 6 items are dead-lettered, W8 guard 3, and other revisions' items are deferred), and its
 * planner and live chain read checkpoints through skiV3CheckpointReadRevisions.
 */
export function skiV3StoreRevision(revision: SkiV3EngineRevision = SKI_V3_ENGINE_REVISION_AT_LOAD): SkiV3EngineRevision {
  return revision;
}

/**
 * The checkpoint revisions a chain writing `storeRevision` resumes from, in order of preference on a
 * tie: its own, and for the alias 6 also revision 3's, whose chain the alias continues (the "v5" mode
 * keeps it running; runbook "Rollback from revision 5"). The live reader takes the latest checkpoint
 * of these inside the look-back. Every read stays keyed by exactly one engine_revision (W8 guard 7).
 */
export function skiV3CheckpointReadRevisions(storeRevision: number): readonly number[] {
  return storeRevision === SKI_V3_R3_ALIAS_REVISION ? [SKI_V3_R3_ALIAS_REVISION, SKI_V3_REVISION] : [storeRevision];
}

/**
 * The chain revisions a store write of `storeRevision` accepts (W8 guard 6, review step 1 B F3): the
 * revision whose checkpoint a run resumed from (or the written revision itself for a cold or bootstrap
 * start, which begins a new chain) must be one of these. Revisions 3 and 5 share one formula and one
 * params version, so only this keeps a revision 5 chain out of revision 3's history and back; the
 * alias 6 accepts its own chain and revision 3's, which it continues.
 */
export function skiV3ChainRevisionsForStore(storeRevision: number): readonly number[] {
  return skiV3CheckpointReadRevisions(storeRevision);
}

/** Why a run of chain `chainRevision` may not be written as `storeRevision` (W8 guard 6), or null. */
export function skiV3ChainRevisionProblem(storeRevision: number, chainRevision: number): string | null {
  if (skiV3ChainRevisionsForStore(storeRevision).includes(chainRevision)) return null;
  return `a revision ${chainRevision} snowpack chain is never written as revision ${storeRevision} ` +
    `(revision ${storeRevision} accepts chains of ${skiV3ChainRevisionsForStore(storeRevision).join(" and ")} only)`;
}

/** The second chain a primary engine revision may run: 5 beside 3 ("v5-shadow"), 3 beside 5 ("v5"), none beside the alias 6. */
export function skiV3ShadowPartner(primary: SkiV3EngineRevision): SkiV3ShadowRevision | null {
  return primary === 3 ? 5 : primary === 5 ? 3 : null;
}

export type SkiV3ShadowRevisionSetting = Readonly<{
  /** The second chain's revision when it runs; null when unset or refused. */
  revision: SkiV3ShadowRevision | null;
  /** The configured value (trimmed), null when unset or empty. */
  raw: string | null;
  /** Why a set value runs no second chain (not 3 or 5, not the primary's partner, or a mode other than "v3"); null otherwise. */
  problem: string | null;
}>;

/**
 * The second chain (SKI_V3_SHADOW_REVISION, W8(a) and (c)) beside the primary engine revision
 * (default: the one this process loaded), in the given mode (default SKI_SCORING_ENGINE). Unset or
 * empty runs none. Exact "3" or "5" (surrounding whitespace ignored) runs it only in "v3" and only
 * beside its partner (skiV3ShadowPartner); any other value, mode or primary runs none and reports
 * why (the live route records the degraded input `ski_v3_shadow_revision`, health warns
 * `ski-v3-shadow-revision-invalid`, the pre-deploy check refuses). It never stamps a row and never
 * stops the published chain: a refused second chain leaves its revision's facts behind, which the
 * W7 flip gate (revision 3 kept as a live shadow) and health catch.
 */
export function skiV3ShadowRevisionSetting(
  env: Env = process.env,
  primary: SkiV3EngineRevision = SKI_V3_ENGINE_REVISION_AT_LOAD,
  mode: SkiScoringEngineMode = skiScoringEngineMode(env),
): SkiV3ShadowRevisionSetting {
  const raw = env[SKI_V3_SHADOW_REVISION_ENV];
  if (raw === undefined || raw.trim() === "") return { revision: null, raw: null, problem: null };
  const value = raw.trim();
  if (value !== "3" && value !== "5") {
    return { revision: null, raw: value, problem: `${SKI_V3_SHADOW_REVISION_ENV}="${value}" is not a shadow revision (3 or 5); no second chain runs` };
  }
  const revision = Number(value) as SkiV3ShadowRevision;
  if (mode !== "v3") {
    return { revision: null, raw: value, problem: `${SKI_V3_SHADOW_REVISION_ENV}=${value} runs only with SKI_SCORING_ENGINE=v3, not "${mode}"; no second chain runs` };
  }
  const partner = skiV3ShadowPartner(primary);
  if (revision !== partner) {
    return {
      revision: null, raw: value,
      problem: partner === null
        ? `${SKI_V3_SHADOW_REVISION_ENV}=${value} beside SKI_V3_ENGINE_REVISION=${primary}: the alias 6 runs no second chain; no second chain runs`
        : `${SKI_V3_SHADOW_REVISION_ENV}=${value} beside SKI_V3_ENGINE_REVISION=${primary}: only ${partner} runs beside ${primary}; no second chain runs`,
    };
  }
  return { revision, raw: value, problem: null };
}

/** The named chain mode of a primary engine revision and its second chain (in "v3"; see the header). */
export function skiV3ChainMode(primary: SkiV3EngineRevision, shadow: SkiV3ShadowRevision | null): SkiV3ChainMode {
  if (primary === SKI_V3_R3_ALIAS_REVISION) return "alias-6";
  if (primary === SKI_V3_REVISION_5) return shadow === SKI_V3_REVISION ? "v5" : "v5-solo";
  return shadow === SKI_V3_REVISION_5 ? "v5-shadow" : "r3";
}

/**
 * The "v2-rollback" revision: 4. Revision 7, drafted for "v2-rollback" after revision 5, is retired
 * (W8(d)), so the answer no longer depends on the engine revision; the argument is kept for callers
 * that name it. The mode itself runs only under engine revision 3 (skiV3EngineRevisionDeployProblem).
 */
export function skiV2RollbackRevisionFor(_revision: SkiV3EngineRevision = skiV3StampEngineRevision()): number {
  return SKI_V2_ROLLBACK_REVISION;
}

/** True for a revision whose rows the v3 engine writes (3, 5 and the alias 6). */
export function isSkiV3FamilyRevision(revision: number): boolean {
  return isSkiV3EngineRevision(revision);
}

/**
 * True when a stored ski hourly curve stamped `revision` came from the v3 engine (3, 5 or the
 * alias 6), whose off-piste curves already follow the engine's lift hours; the ski detail page
 * normalises only older curves (lib/conditions/unified-detail.ts readableStoredCurve). Anything
 * that is not a v3 family revision number, a missing stamp included, is an older curve.
 */
export function skiStoredCurveFollowsLiftHours(revision: unknown): boolean {
  return typeof revision === "number" && isSkiV3FamilyRevision(revision);
}

/** Exact values "v2", "shadow", "v3" or "v2-rollback" (surrounding whitespace ignored); anything else is "shadow". */
export function skiScoringEngineMode(env: Env = process.env): SkiScoringEngineMode {
  return skiScoringEngineSetting(env).mode;
}

export function isSkiRevisionedSport(sport: string): sport is SkiRevisionedSport {
  return sport === "piste" || sport === "off-piste" || sport === "touring";
}

/** True for revisions whose scores come from the legacy engines (2, 4 and the retired 7). */
export function isSkiLegacyEngineRevision(revision: number): boolean {
  return SKI_LEGACY_ENGINE_REVISIONS.includes(revision);
}

/**
 * Revision a ski sport is stamped with in the given mode under the given v3
 * engine revision: the v3 engine revision in "v3" (3 by default, 5, or the
 * alias 6), 4 in "v2-rollback" (revision 7 is retired, W8(d)), otherwise 2.
 * The writers pass skiV3StampEngineRevision's value.
 */
export function skiPublishRevisionForMode(mode: SkiScoringEngineMode, v3Revision: SkiV3EngineRevision = DEFAULT_SKI_V3_ENGINE_REVISION): number {
  if (mode === "v3") return v3Revision;
  return mode === "v2-rollback" ? skiV2RollbackRevisionFor(v3Revision) : SKI_V2_REVISION;
}

/**
 * Revision the writers stamp for piste, off-piste or touring in the current
 * mode (2 in "v2" and "shadow", the v3 engine revision in "v3", 4 in
 * "v2-rollback"). `SPORT_SCORING[sport].version` holds the same value, fixed
 * when the process loaded; prefer that constant in code that already reads
 * `SPORT_SCORING` so tests that simulate a revision bump keep working.
 */
export function skiPublishRevision(sport: SkiRevisionedSport, env: Env = process.env): number {
  if (!isSkiRevisionedSport(sport)) throw new Error(`Not a revisioned ski sport: ${String(sport)}`);
  return skiPublishRevisionForMode(skiScoringEngineMode(env), skiV3StampEngineRevision(skiV3EngineRevision(env)));
}

/** True when the v3 engine runs at all (shadow or publish); never in "v2" or "v2-rollback". */
export function skiV3EngineRuns(mode: SkiScoringEngineMode = skiScoringEngineMode()): mode is SkiV3RunningMode {
  return mode === "shadow" || mode === "v3";
}

/** True when the v3 engine produces the published scores. */
export function skiV3EnginePublishes(mode: SkiScoringEngineMode = skiScoringEngineMode()): boolean {
  return mode === "v3";
}

/**
 * Production recovery slot replay claim (lane feat/p3i-rev5-mechanics). "off" (exactly, surrounding
 * whitespace ignored) leaves the `replay` kind out of /api/cron/conditions-recovery's handlers, so
 * the deployment claims no replay item at all while a revision 5 replay runs on the revision 5
 * executors (local scripts/replay-ski-v3.ts --work started with SKI_V3_ENGINE_REVISION=5 in the
 * shell, or the conditions-replay workflow with its ski_v3_engine_revision input set to 5;
 * recoveryWorkHandlers below). The flag removes the whole kind, family replays (coastal, sailing,
 * mtb) included: while it is off they drain only on executors started with
 * CONDITIONS_REPLAY_FAMILIES (runbook "Revision 5" step 2).
 * Unset or any other value keeps today's behaviour. scripts/replay-ski-v3.ts refuses to enqueue revision 5 items until the latest
 * conditions ski slice reports the flag off (or the revision 5 engine), and the ski replay handler
 * defers, rather than dead-letters, an item of a revision its process does not run.
 */
export const RECOVERY_REPLAY_CLAIM_FLAG = "CONDITIONS_RECOVERY_REPLAY_CLAIM";

export function recoveryReplayClaimEnabled(env: Env = process.env): boolean {
  return env[RECOVERY_REPLAY_CLAIM_FLAG]?.trim() !== "off";
}

/**
 * The recovery slot's work handlers without `replay` while CONDITIONS_RECOVERY_REPLAY_CLAIM is
 * "off" (the dispatcher claims only the kinds it has handlers for); otherwise the same object.
 */
export function recoveryWorkHandlers<T extends Readonly<Record<string, unknown>>>(handlers: T, env: Env = process.env): T {
  if (recoveryReplayClaimEnabled(env)) return handlers;
  const rest: Record<string, unknown> = { ...handlers };
  delete rest.replay;
  return rest as T;
}
